/**
 * La factura extranjera que corresponde a una partida.
 *
 * `foreignInvoice.amount` es el TOTAL de la factura, uno solo, pero la misma factura
 * cuelga de TODAS las entradas de la partida: recorrer `entries.bills` la devuelve
 * repetida. Sumarla una vez por entrada —y encima mezclar otras facturas pegadas a esas
 * mismas entradas— daba múltiplos del importe real.
 *
 * Caso de prod del 2026-10-09, factura MCI28926-10082445: la posición del pedido
 * 4517583824 tiene dos entradas con esa factura más otra factura vieja de 102,240
 * colgada de la 5122567081, así que la pantalla mandó al aprobar
 *
 *     100,310.40 × 2 + 102,240 = 302,860.80
 *
 * El backend lo leyó como una edición del renglón, le sumó las otras dos posiciones y
 * guardó 454,924.80, que es el `Valor_neto` que recibió SAP contra un `Valor_bruto` de
 * 249,984.
 *
 * Se queda con una sola factura por id, descarta las rechazadas y devuelve la más
 * reciente: el mismo criterio que usa el backend al aprobar (`getUUIDBills`).
 */
export const foreignBillOf = (item) => {
    const porFactura = new Map();

    (item?.entries || []).forEach((entry) => {
        (entry?.bills || []).forEach((bill) => {
            if (!bill || !bill.foreignInvoice || bill.billStatus === 'REJECTED') return;
            const clave = bill.id || bill.cfdiUuid;
            if (!porFactura.has(clave)) porFactura.set(clave, bill);
        });
    });

    return [...porFactura.values()].sort(
        (a, b) =>
            new Date(b.foreignInvoice.invoiceDate).getTime() -
            new Date(a.foreignInvoice.invoiceDate).getTime()
    )[0];
};

/** ¿Son la misma factura? El UUID manda; el id es el desempate cuando no hay UUID. */
const mismaFactura = (una, otra) =>
    (una?.cfdiUuid && una.cfdiUuid === otra?.cfdiUuid) || (una?.id && una.id === otra?.id);

/**
 * Las entradas que ampara una factura, no todas las de la partida.
 *
 * Una posición de entregas recurrentes acumula una entrada por mes y conserva las de
 * meses anteriores, cada una con su propia factura ya pagada. La pantalla tomaba
 * `entries[0]`, que es la MÁS VIEJA, y de ahí salían el número de entrada, el importe
 * de referencia y el rango de la edición.
 *
 * Caso de prod del 2026-10-12, pedido 4517402276 posición 10 (DESCARTES SYSTEMS): siete
 * entradas desde abril. Se facturaba la de octubre, 5124066685 por 7,301.72, y la fila
 * mostraba la de abril, 5119904257 por 6,858.51. Al editar el importe el rango permitido
 * era 3,429.26 – 7,058.51, que ni siquiera contiene el importe correcto.
 *
 * Sin factura con la que cruzar se conservan todas las entradas: es lo que el backend
 * usa como referencia al aprobar (`PurchaseOrder.foreignGoodsReceipts`).
 */
export const entriesOfBill = (item, bill) => {
    const entries = item?.entries || [];
    if (!bill) return entries;

    const propias = entries.filter((entry) =>
        (entry?.bills || []).some((suya) => mismaFactura(suya, bill))
    );

    return propias.length ? propias : entries;
};

/** El importe de entradas contra el que se mide la factura. */
export const goodsReceiptsOf = (item, bill) =>
    Number(
        entriesOfBill(item, bill)
            .reduce((total, entry) => total + Number(entry?.amount || 0), 0)
            .toFixed(2)
    );

/** El número de entrada que se está facturando, o los números si la factura cubre varias. */
export const entrySheetOf = (item, bill) =>
    entriesOfBill(item, bill)
        .map((entry) => entry?.number)
        .filter(Boolean)
        .join(', ');

/** Lo que se permite de más sobre las entradas, en pesos. */
export const UPPER_INVOICE_TOLERANCE = 200;

/** Su equivalente en cualquier otra moneda. */
export const UPPER_INVOICE_TOLERANCE_FOREIGN = 10;

/**
 * La tolerancia que corresponde a una moneda, la misma tabla que aplica el backend
 * (`invoice-amount-tolerance.util.ts`): 200 en pesos, 10 en cualquier otra.
 *
 * Los 200 estaban quemados y sin moneda, así que en dólares la pantalla aceptaba una
 * edición de hasta +200 USD, la mandaba, y el backend la descartaba en silencio: el
 * interno creía haber guardado y el importe seguía igual.
 *
 * Una moneda desconocida cae en la estrecha, como en el backend.
 */
export const upperToleranceFor = (currency) =>
    currency === 'MXN' ? UPPER_INVOICE_TOLERANCE : UPPER_INVOICE_TOLERANCE_FOREIGN;
